// ============================================================
// Focus trap + Esc handler for drawers and modal — the a11y addition
// the spec explicitly calls out: "the prototype omits trapping — add it."
// ============================================================
import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Trap Tab/Shift+Tab inside `containerRef` while `open` is true, and:
 *  - move focus into the container on open (first focusable, or the container itself),
 *  - call `onClose` when Esc is pressed,
 *  - return focus to the previously-focused element on close.
 *
 * Pass the same `open`/`onClose` you'd give the drawer/modal — the hook handles
 * the rest. Safe when `containerRef.current` is null (e.g. before mount).
 */
export function useFocusTrap<T extends HTMLElement>(
  containerRef: RefObject<T | null>,
  open: boolean,
  onClose: () => void,
) {
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const container = containerRef.current;
    if (!container) return;

    previouslyFocused.current = (document.activeElement as HTMLElement) ?? null;

    // Move focus into the container.
    const focusables = container.querySelectorAll<HTMLElement>(FOCUSABLE);
    const first = focusables[0];
    (first ?? container).focus();

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;

      // Re-query in case DOM changed.
      const items = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (e.shiftKey && active === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      // Return focus to whatever triggered the open. Best-effort: the element
      // might have unmounted by now.
      previouslyFocused.current?.focus?.();
    };
  }, [open, containerRef, onClose]);
}
